import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonSpinner
} from '@ionic/react';
import { mail, document, calendar } from 'ionicons/icons';

import { configService } from '../services/ConfigService';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './TermsOfService.css';

function TermsOfService() {
  const [legalContent, setLegalContent] = useState(null);
  const baseMeta = seoService.getPageMeta('home');
  const [loading, setLoading] = useState(true);
  const config = configService.getConfig();
  const appInfo = config?.app || {};

  useEffect(() => {
    loadLegalContent();
  }, []);

  const loadLegalContent = async () => {
    try {
      const response = await fetch('/assets/configs/common/legal.json');
      const data = await response.json();
      setLegalContent(data);
    } catch (error) {
      console.error('Failed to load legal content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Replace placeholders with actual app values
  const replacePlaceholders = (text) => {
    if (!text) return '';
    return text
      .replace(/{APP_NAME}/g, appInfo.name || 'the Application')
      .replace(/{SUPPORT_EMAIL}/g, appInfo.supportEmail || 'support@example.com')
      .replace(/{AUTHOR}/g, appInfo.author || 'the Developer');
  };

  const openSupport = () => {
    const email = appInfo.supportEmail || 'support@example.com';
    window.open(`mailto:${email}?subject=Terms Inquiry - ${appInfo.name || 'App'}`, '_system');
  };

  if (loading) {
    return (
      <>
        <SEO
          title="Terms of Service"
          description="Read our terms of service for using the Learn Coding application."
          ogImage={baseMeta.ogImage}
          noIndex={true}
        />
        <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/about" />
            </IonButtons>
            <IonTitle>Terms of Service</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="loading-container">
            <IonSpinner />
            <p>Loading...</p>
          </div>
          </IonContent>
        </IonPage>
      </>
    );
  }

  const termsOfService = legalContent?.termsOfService;

  return (
    <>
      <SEO
        title="Terms of Service"
        description="Read our terms of service for using the Learn Coding application."
        ogImage={baseMeta.ogImage}
        noIndex={true}
      />
      <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/about" />
          </IonButtons>
          <IonTitle>Terms of Service</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div className="legal-container">
          {/* Header */}
          <div className="legal-header">
            <div className="legal-icon terms-icon">
              <IonIcon icon={document} />
            </div>
            <h1>Terms of Service</h1>
            <p className="app-name">{appInfo.name || 'Application'}</p>
            {termsOfService?.lastUpdated && (
              <div className="last-updated">
                <IonIcon icon={calendar} />
                <span>Last Updated: {termsOfService.lastUpdated}</span>
              </div>
            )}
          </div>

          {/* Content Sections */}
          <div className="legal-content">
            {termsOfService?.sections?.map((section, index) => (
              <IonCard key={index} className="legal-section-card">
                <IonCardContent>
                  <h2 className="section-title">
                    <span className="section-number">{index + 1}.</span>
                    {section.title}
                  </h2>
                  <div className="section-content">
                    {replacePlaceholders(section.content).split('\n').map((paragraph, pIndex) => (
                      <p key={pIndex}>{paragraph}</p>
                    ))}
                  </div>
                </IonCardContent>
              </IonCard>
            ))}
          </div>

          {/* Contact Section */}
          <div className="contact-section">
            <h3>Questions?</h3>
            <p>If you have any questions about these Terms of Service, please contact us.</p>
            <IonButton expand="block" onClick={openSupport}>
              <IonIcon icon={mail} slot="start" />
              Contact Us
            </IonButton>
          </div>

          {/* Footer */}
          <div className="legal-footer">
            <p>© {new Date().getFullYear()} {appInfo.author || 'Developer'}. All rights reserved.</p>
          </div>
        </div>
      </IonContent>
    </IonPage>
    </>
  );
}

export default TermsOfService;